
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Tables } from "@/integrations/supabase/types";

type Activity = Tables<"activities">;
type ActivityInsert = {
  title: string;
  description?: string;
  type: "worksheet" | "game" | "craft" | "video" | "interactive";
  icon?: string;
  color?: string;
  difficulty?: "easy" | "medium" | "hard";
  age_group?: string;
  download_url?: string;
  status?: "draft" | "published" | "archived";
  category_id?: string;
};

export const useActivityOperations = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchActivities = async () => {
    try {
      const { data, error } = await supabase
        .from("activities")
        .select(`
          *,
          activity_categories(
            id,
            name,
            color,
            icon
          )
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setActivities(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to fetch activities: " + error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveActivity = async (activity: ActivityInsert, editingActivityId?: string) => {
    try {
      if (editingActivityId) {
        const { error } = await supabase
          .from("activities")
          .update(activity)
          .eq("id", editingActivityId);

        if (error) throw error;
        toast({ title: "Success", description: "Activity updated successfully!" });
      } else {
        const { error } = await supabase
          .from("activities")
          .insert(activity);

        if (error) throw error;
        toast({ title: "Success", description: "Activity created successfully!" });
      }

      fetchActivities();
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteActivity = async (activityId: string) => {
    if (!confirm("Are you sure you want to delete this activity? This action cannot be undone.")) {
      return false;
    }

    try {
      // First, get the activity to check if it has an uploaded file
      const { data: activity } = await supabase
        .from("activities")
        .select("download_url")
        .eq("id", activityId)
        .single();

      // If there's an uploaded file, delete it from storage
      if (activity?.download_url && activity.download_url.startsWith('activities/')) {
        await supabase.storage
          .from('activities')
          .remove([activity.download_url]);
      }

      // Delete the activity record
      const { error } = await supabase
        .from("activities")
        .delete()
        .eq("id", activityId);

      if (error) throw error;

      toast({ title: "Success", description: "Activity deleted successfully!" });
      fetchActivities();
      return true;
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to delete activity: " + error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    activities,
    loading,
    fetchActivities,
    saveActivity,
    deleteActivity,
  };
};
